import React, { useEffect, useMemo, useState } from 'react';

import { useMobile } from '../../../hooks/useMobile';

import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../../../icons/chevron-right.svg';
import { ReactComponent as EmptyResultIcon } from '../../../icons/empty-result.svg';
import { ReactComponent as ErrorResultIcon } from '../../../icons/error-result.svg';
import { ReactComponent as ExportIcon } from '../../../icons/export.svg';
import { ReactComponent as FilterIcon } from '../../../icons/filter.svg';

import './table.scss';

import { exportTableToCSV, getPageNumbers } from '../../../utils/tools';
import PageLoader from '../../loader/PageLoader';
import { Button } from '../button/Button';
import { SearchInput } from '../search-input/SearchInput';
import { Select } from '../select/Select';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel
} from '@tanstack/react-table';

export const Table = ({
  id,
  columns,
  data,
  action,
  actionIcon: ActionIcon,
  onActionClick,
  filters = [],
  loading = false,
  error = false,
  canExport = true,
  showAll = false,
  showHeader = true,
  onRowClick = () => {}
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: showAll ? Number.MAX_SAFE_INTEGER : 10
  });

  const isMobile = useMobile();

  const [columnFilters, setColumnFilters] = useState([]);
  const [selectedExportFields, setSelectedExportFields] = useState([]);

  const tableColumns = useMemo(() => {
    return columns.map((column) => {
      const filterConfig = filters.find((f) => f.key === column.accessorKey || f.key === column.id);

      if (filterConfig) {
        return {
          ...column,
          filterFn: 'tableFilterFunction',
          meta: { logic: filterConfig.logic || 'OR' }
        };
      }
      return column;
    });
  }, [columns, filters]);

  const filterLogicMap = useMemo(() => {
    const map = {};
    filters.forEach((f) => {
      map[f.key] = f.logic || 'OR';
    });
    return map;
  }, [filters]);

  const tableFilterFunctionFn = (row, columnId, filterValues) => {
    if (!filterValues || filterValues.length === 0) {
      return true;
    }

    const logic = filterLogicMap[columnId] || 'OR';

    const rowRawValue = row.getValue(columnId);
    const rowValue =
      rowRawValue && typeof rowRawValue === 'object' && 'value' in rowRawValue
        ? rowRawValue.value
        : rowRawValue;

    // Normalize filterValues and rowValue to IDs or primitives
    const rowId = rowValue && typeof rowValue === 'object' ? (rowValue.id ?? rowValue) : rowValue;

    if (logic === 'AND') {
      return filterValues.every((filterVal) => {
        const filterId =
          filterVal && typeof filterVal === 'object' ? (filterVal.id ?? filterVal) : filterVal;
        return filterId === rowId;
      });
    } else {
      return filterValues.some((filterVal) => {
        const filterId =
          filterVal && typeof filterVal === 'object' ? (filterVal.id ?? filterVal) : filterVal;
        return filterId === rowId;
      });
    }
  };

  const table = useReactTable({
    data,
    columns: tableColumns, // Use the dynamically configured columns
    getRowId: (row, index) => `${id}_${row.id}_${index}`,
    state: {
      globalFilter,
      pagination,
      columnFilters
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      tableFilterFunction: tableFilterFunctionFn
    }
  });

  const exportColumns = useMemo(() => {
    const orderedColumns = table.getAllLeafColumns();

    return [
      {
        key: 'columns',
        label: 'Columns',
        options: orderedColumns
          .filter((col) => !col.columnDef.disableExport)
          .map((col, index) => ({
            label: col.columnDef.header,
            value: col.id,
            sortIndex: index
          }))
      }
    ];
  }, [table]);

  const handleMultiColumnFilterChange = (e) => {
    const selectedValues = e.target.value;

    const newColumnFiltersMap = {};
    selectedValues.forEach((val) => {
      const filterGroup = filters.find((f) => f.options.some((o) => o.value === val));
      if (filterGroup) {
        const columnKey = filterGroup.key;
        if (!newColumnFiltersMap[columnKey]) {
          newColumnFiltersMap[columnKey] = [];
        }
        newColumnFiltersMap[columnKey].push(val);
      }
    });

    const updatedColumnFilters = Object.entries(newColumnFiltersMap).map(([id, value]) => ({
      id,
      value
    }));
    setColumnFilters(updatedColumnFilters);
  };

  const handleMultiColumnSelectChange = (e) => {
    const selectedValues = e.target.value;

    const exportOptions = exportColumns[0]?.options || [];

    const valueOrderMap = Object.fromEntries(exportOptions.map((opt, index) => [opt.value, index]));

    const sortedValues = [...selectedValues].sort((a, b) => valueOrderMap[a] - valueOrderMap[b]);

    const grouped = {};

    sortedValues.forEach((val) => {
      const columnKey =
        exportOptions.find((o) => o.value === val)?.columnKey || exportColumns[0].key;

      if (!columnKey) return;

      grouped[columnKey] = grouped[columnKey] || [];
      grouped[columnKey].push(val);
    });

    setSelectedExportFields(grouped);
  };

  useEffect(() => {
    const totalFilteredRows = table.getFilteredRowModel().rows.length;
    const newPageCount = Math.ceil(totalFilteredRows / pagination.pageSize);

    if (pagination.pageIndex >= newPageCount && newPageCount > 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    } else if (newPageCount === 0 && pagination.pageIndex > 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [table, pagination.pageSize, pagination.pageIndex]);

  const pages = useMemo(() => {
    return getPageNumbers({
      getPageCount: () => table.getPageCount(),
      getState: () => ({ pagination })
    });
  }, [table, pagination]);

  return (
    <div className="table">
      {showHeader && (
        <div className="table__header">
          {action && (
            <Button
              style={{ width: 'fit-content' }}
              onClick={onActionClick}
              secondary
              className="table__action"
            >
              <ActionIcon width={20} height={20} />
              <span>{action}</span>
            </Button>
          )}
          <SearchInput
            style={{ width: '300px' }}
            name="table-search"
            placeholder="Search"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.value)}
          />
          <div className="table__actions">
            {filters.length > 0 && (
              <div className="table__filter">
                <Select
                  label="Filter"
                  dropdownWidth={300}
                  options={filters}
                  multiSelect
                  icon={FilterIcon}
                  showAsIcon
                  showSelectCount
                  value={columnFilters.flatMap((filter) => filter.value)}
                  onChange={handleMultiColumnFilterChange}
                  action="Clear Filter"
                  onActionClick={() => setColumnFilters([])}
                />
              </div>
            )}
            {canExport && (
              <Select
                label="Export"
                dropdownWidth={300}
                multiSelect
                showAsIcon
                icon={ExportIcon}
                value={Object.values(selectedExportFields).flat()}
                onChange={handleMultiColumnSelectChange}
                action="Export"
                onActionClick={() => {
                  const flatExportFields = Object.values(selectedExportFields).flat();
                  exportTableToCSV(table, flatExportFields);
                }}
                options={exportColumns}
              />
            )}
          </div>
        </div>
      )}
      <table key={id}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    minWidth: `${header.column.columnDef.minSize}px`,
                    maxWidth: `${header.column.columnDef.maxSize}px`,
                    width: `${header.getSize()}px`
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          className={
            table.getFilteredRowModel().rows.length > 0 ? 'table__body' : 'table__body--empty'
          }
        >
          <PageLoader loading={loading} table>
            {table
              .getPaginationRowModel()
              .rows.map((row) =>
                isMobile ? (
                  <RowMobile table={table} row={row} onRowClick={onRowClick} error={error} />
                ) : (
                  <RowDesktop table={table} row={row} onRowClick={onRowClick} error={error} />
                )
              )}
            {!error && table.getPaginationRowModel().rows.length === 0 && (
              <tr className="table__empty-state">
                <td colSpan={tableColumns.length}>
                  <EmptyResultIcon />
                  <span>No results found.</span>
                </td>
              </tr>
            )}
            {error && (
              <tr className="table__empty-state table__error">
                <td colSpan={tableColumns.length}>
                  <ErrorResultIcon />
                  <span>There was an error fetching your data.</span>
                </td>
              </tr>
            )}
          </PageLoader>
        </tbody>
      </table>
      {!showAll && (
        <Pagination
          pages={pages}
          totalRows={table.getFilteredRowModel().rows.length}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          onItemClick={(index) => table.setPageIndex(index)}
          setPageSize={(size) => table.setPageSize(size)}
          onNextClick={() => table.nextPage()}
          onPreviousClick={() => table.previousPage()}
          canNext={table.getCanNextPage()}
          canPrevious={table.getCanPreviousPage()}
        />
      )}
    </div>
  );
};

const RowDesktop = ({ row, onRowClick, error }) => (
  <tr key={row.id} onClick={() => onRowClick(row.original)}>
    {!error &&
      row.getVisibleCells().map((cell) => {
        const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());

        // If the rendered result is just the value, and it's a { label, value } object:
        const value = cell.getValue();
        const isLabelValueObject =
          value && typeof value === 'object' && 'label' in value && 'value' in value;

        return (
          <td
            key={cell.id}
            style={{
              minWidth: `${cell.column.columnDef.minSize}px`,
              maxWidth: `${cell.column.columnDef.maxSize}px`,
              width: `${cell.column.getSize()}px`
            }}
          >
            {isLabelValueObject ? value.label : rendered}
          </td>
        );
      })}
  </tr>
);

const RowMobile = ({ table, row, onRowClick, error }) => (
  <tr key={row.id} onClick={() => onRowClick(row.original)} className="table__row">
    {/* Mobile headers */}
    <div className="table__mobile-head">
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <th key={header.id}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </th>
          ))}
        </tr>
      ))}
    </div>

    {/* Mobile + Desktop values */}
    <div className="table__mobile-body">
      {!error &&
        row.getVisibleCells().map((cell) => {
          const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
          const value = cell.getValue();
          const isLabelValueObject =
            value && typeof value === 'object' && 'label' in value && 'value' in value;

          return <td key={cell.id}>{isLabelValueObject ? value.label : rendered}</td>;
        })}
    </div>
  </tr>
);

const Pagination = ({
  pages,
  totalRows,
  pageIndex,
  pageSize,
  setPageSize,
  onItemClick,
  onNextClick,
  onPreviousClick,
  canNext,
  canPrevious
}) => (
  <div className="table__pagination">
    {pages.length > 0 && (
      <div className="table__pagination-size">
        <Select
          dropdownWidth={60}
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          options={[10, 20, 50].map((size) => ({ value: size, label: `${size}` }))}
        />
        <span>per page</span>
      </div>
    )}
    <div>
      {/* Prev Button */}
      {pages.length > 1 && (
        <button
          onClick={onPreviousClick}
          disabled={!canPrevious}
          className="table__pagination-button"
        >
          <ChevronLeftIcon />
        </button>
      )}
      {pages.map((page, index) =>
        page === '...' ? (
          <span key={index} className="table__pagination-number">
            ...
          </span>
        ) : (
          <button
            key={`pagination-${page}`}
            onClick={() => onItemClick(page - 1)}
            className={`table__pagination-number ${
              pageIndex === page - 1 ? 'table__pagination-number--active' : ''
            }`}
          >
            {page}
          </button>
        )
      )}
      {/* Next Button */}
      {pages.length > 1 && (
        <button onClick={onNextClick} disabled={!canNext} className="table__pagination-button">
          <ChevronRightIcon />
        </button>
      )}
    </div>
    {pages.length > 0 && (
      <div className="table__pagination-count">
        {(() => {
          const start = pageIndex * pageSize + 1;
          const end = Math.min(start + pageSize - 1, totalRows);
          return `${start} – ${end} of ${totalRows}`;
        })()}
      </div>
    )}
  </div>
);
